import { AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CardBrandIcon } from "@/components/billing/CardBrandIcon";
import { formatDate } from "@/lib/format";
import type { MySubscription, ViewQuota } from "@/types/domain";

const CARD =
  "rounded-2xl border border-slate-100 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900";

// Los tres estados posibles de la suscripción y cómo se presentan.
const STATE_META = {
  expired: {
    label: "Vencido",
    className: "text-red-600 dark:text-red-400",
    dateLabel: "Venció el",
  },
  cancelled: {
    label: "Cancelado",
    className: "text-amber-600 dark:text-amber-400",
    dateLabel: "Acceso hasta el",
  },
  active: {
    label: "Vigente",
    className: "text-emerald-600 dark:text-emerald-400",
    dateLabel: "Próxima renovación",
  },
} as const;

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-slate-100 py-3 last:border-0 dark:border-slate-800">
      <span className="text-sm text-slate-400 dark:text-slate-500">{label}</span>
      <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
        {value}
      </span>
    </div>
  );
}

export function CurrentPlanPanel({
  subscription,
  quota,
  expired,
  onUpdateCard,
  onDowngradeToFree,
  isUpdatingCard,
}: {
  subscription: MySubscription;
  quota?: ViewQuota;
  expired: boolean;
  onUpdateCard: () => void;
  onDowngradeToFree: () => void;
  isUpdatingCard: boolean;
}) {
  const { cancelAtPeriodEnd, isManaged } = subscription;
  // El plan gratuito no vence ni se cobra: no tiene sentido hablarle de renovación.
  const isFree = subscription.priceAmount === 0;
  const state = expired ? "expired" : cancelAtPeriodEnd ? "cancelled" : "active";

  return (
    <div className={CARD}>
      <div className="mb-5">
        <h2 className="text-base font-extrabold text-slate-800 dark:text-slate-100">
          Tu plan
        </h2>
        <p className="mt-0.5 text-sm text-slate-400 dark:text-slate-500">
          Estado de tu acceso a Licitigo
        </p>
      </div>

      <Row label="Plan" value={subscription.planName} />
      <Row
        label="Estado"
        value={<span className={STATE_META[state].className}>{STATE_META[state].label}</span>}
      />
      {isFree ? (
        <Row label="Vigencia" value="Gratis para siempre" />
      ) : (
        <Row
          label={STATE_META[state].dateLabel}
          value={formatDate(
            // Solo un plan vigente tiene siguiente cobro; si no, manda la fecha de corte.
            state === "active" && subscription.nextBillingAt
              ? subscription.nextBillingAt
              : subscription.expiresat,
          )}
        />
      )}
      <Row
        label="Vistas este mes"
        value={
          quota?.unlimited
            ? "Ilimitadas"
            : quota
              ? `${quota.remaining} de ${quota.limit} disponibles`
              : "—"
        }
      />
      <Row
        label="Líneas de negocio"
        value={
          subscription.maxSearchProfiles === null
            ? "Ilimitadas"
            : `Hasta ${subscription.maxSearchProfiles}`
        }
      />
      {subscription.card && (
        <Row
          label="Tarjeta"
          value={
            <span className="flex items-center gap-2">
              <CardBrandIcon brand={subscription.card.brand} />
              ···· {subscription.card.lastFour}
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0"
                disabled={isUpdatingCard}
                onClick={onUpdateCard}
              >
                {isUpdatingCard && <Loader2 className="size-3 animate-spin" />}
                Cambiar
              </Button>
            </span>
          }
        />
      )}

      {subscription.state === "PAYMENT_FAILED" && (
        <div className="mt-5 flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-950/30">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
            <p className="text-sm text-amber-900 dark:text-amber-200">
              No pudimos cobrar tu tarjeta. Sigues con acceso completo hasta el{" "}
              <strong>{formatDate(subscription.expiresat)}</strong>; después tu
              cuenta pasa al plan Free.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 pl-7">
            <Button size="sm" disabled={isUpdatingCard} onClick={onUpdateCard}>
              {isUpdatingCard && <Loader2 className="size-3 animate-spin" />}
              Cambiar tarjeta
            </Button>
            <Button variant="ghost" size="sm" onClick={onDowngradeToFree}>
              Pasar al plan Free
            </Button>
          </div>
        </div>
      )}

      {cancelAtPeriodEnd && !expired && (
        <div className="mt-5 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-950/30">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <p className="text-sm text-amber-900 dark:text-amber-200">
            No renovaremos tu plan. Conservas el acceso hasta el{" "}
            <strong>{formatDate(subscription.expiresat)}</strong>; puedes volver
            a contratar cuando quieras.
          </p>
        </div>
      )}

      {!isManaged && !isFree && (
        <p className="mt-5 text-xs text-slate-400 dark:text-slate-500">
          Este plan fue asignado por nuestro equipo. Para modificarlo, escríbenos
          a hola@licitigo.com.
        </p>
      )}
    </div>
  );
}
